<template>
  <section class="ip-card" data-testid="impact-preview-card">
    <header class="card-header">
      <h2 class="card-title">Impact preview</h2>
      <p class="card-subtitle">What the unsaved changes would do to today’s accounts.</p>
    </header>

    <div v-if="loading" class="ip-state" data-testid="impact-preview-loading">Calculating…</div>

    <div v-else-if="error" class="ip-state ip-state--error" data-testid="impact-preview-error">
      {{ error }}
    </div>

    <div v-else-if="preview" class="ip-body" data-testid="impact-preview-body">
      <div class="ip-counts">
        <div
          v-for="band in BANDS"
          :key="band.key"
          class="ip-count"
          :data-testid="`impact-preview-${band.key}`"
        >
          <span
            class="ip-count-badge"
            :style="{ color: band.text, background: band.bg, borderColor: band.border }"
          >
            {{ preview[band.key] ?? 0 }} accounts
          </span>
          <span class="ip-count-label">{{ band.label }}</span>
        </div>
      </div>

      <p class="ip-delta" data-testid="impact-preview-delta">
        {{ preview.change || "No change from current version." }}
      </p>
    </div>
  </section>
</template>

<script setup>
import { defineProps, onBeforeUnmount, ref, watch } from "vue";
import { previewScoringImpact } from "@/api";

const props = defineProps({
  draft: {
    type: Object,
    required: true,
  },
});

const BANDS = [
  { key: "sticky", label: "Sticky", text: "#067647", bg: "#ecfdf3", border: "#abefc6" },
  { key: "watch", label: "Watch", text: "#b54708", bg: "#fffaeb", border: "#fedf89" },
  { key: "stalling", label: "Stalling", text: "#b42318", bg: "#fef3f2", border: "#fecdca" },
];

const preview = ref(null);
const loading = ref(false);
const error = ref("");
let timer = null;

async function runPreview() {
  loading.value = true;
  error.value = "";
  try {
    const response = await previewScoringImpact(props.draft);
    preview.value = response?.data || null;
  } catch (err) {
    error.value = "Could not load impact preview.";
    console.error("Failed to load adoption impact preview:", err);
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.draft,
  () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(runPreview, 500);
  },
  { deep: true, immediate: true }
);

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});
</script>

<style scoped>
.ip-card {
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
}

.card-header {
  margin-bottom: 16px;
}

.card-title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.card-subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.ip-state {
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.ip-state--error {
  color: #b42318;
}

.ip-counts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ip-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ip-count-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.ip-count-label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
}

.ip-delta {
  margin: 16px 0 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}
</style>
